import { yupResolver } from '@hookform/resolvers/yup'
import { useDisabledPromoCodesList } from '@if/core'
import { compareAsc, DateOnly } from '@if/core/date'
import {
  EDocumentStatus,
  EDocumentType,
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentMethod,
  EPaymentPlan,
  EPlanType,
  EProvince,
  EWorksheetStatus,
  getEPaymentMethodList,
} from '@if/core/types'
import { formatCurrency, formatDate, normalizeDateTime, normalizeNumber } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import { Delete, ErrorOutline, Lock } from '@mui/icons-material'
import InfoOutlineIcon from '@mui/icons-material/InfoOutline'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  Breadcrumb,
  IconButtonWithTooltip,
  InputTextField,
  PageError,
  PageSpinner,
  SelectComponent,
} from '@src/components'
import HighEstimateDialog from '@src/components/HighEstimateDialog'
import SelectMerchantDialog from '@src/components/SelectMerchantDialog'
import MerchantPaymentInstructions from '@src/containers/ViewCreditApplicationPage/components/MerchantPaymentInstructions'
import PrivateMessage from '@src/containers/ViewCreditApplicationPage/components/privateMessage'
import {
  useMaxPaymentAmountWithAgentLeeway,
  useMaxPaymentError,
} from '@src/containers/ViewWorksheet/viewWorksheet-hooks'
import {
  canComputeFunding,
  getEmptyComputedFundingDto,
  getHighCostRateForProvince,
} from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useGetMerchantById, useGetMerchantsByIds } from '@src/data/api/merchant/merchant-api'
import { useDeleteMessage } from '@src/data/api/message/message-api'
import {
  useCompleteFinalizeHighEstimate,
  useComputePersonalLoanFunding,
  useUpdatePersonalLoanWorksheet,
} from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useValidTerms } from '@src/data/hooks/terms-hooks'
import {
  useBusinessErrors,
  useFeatureFlags,
  useFinancingConfig,
  useProvinceConfigurations,
  useSetBusinessErrors,
} from '@src/data/selectors/app-config-selectors'
import { useHasCVT } from '@src/data/selectors/credit-selectors'
import {
  buildEditPersonalLoanWorksheetDtoSchema,
  Constants,
  CreditApplicationMessage,
  EditPersonalLoanWorksheetDto,
  EditWorksheetPaymentSplitDto,
  EPaymentMethodMerchants,
  InternalMerchants,
  Merchant,
  PersonalLoanWorksheet,
  SelectValueListItem,
} from '@src/data/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { MerchantPayment } from '@src/data/types/MerchantPayment'
import { usePreferences } from '@src/providers/PreferencesProvider'
import { useUser } from '@src/providers/UserProvider'
import { reportErrorToConsole } from '@src/services/error-logger'
import { formatPaymentPlanForSummary } from '@src/services/Formatter'
import { computeTotalCoverageFromFunding } from '@src/services/WorksheetComputer'
import { debounce, sumBy } from 'lodash-es'
import { nanoid } from 'nanoid'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import AvailableCreditTooltip from '../../../components/AvailableCreditTooltip'
import {
  useAprTooHigh,
  useEditTotalObligation,
  useFirstPaymentDateOptions,
  usePaymentPlanList,
  usePossibleTerms,
} from '../editWorksheet-hooks'
const PAY_CUSTOMER_IDS: string[] = [EPaymentMethodMerchants.PayCustomerByBankTransfer, EPaymentMethodMerchants.PayCustomerByCheque]

const EditPersonalLoanWorksheetPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  const { creditApplication, isLoading: isLoadingCreditApplication } = useCreditApplicationData()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')
  const programFinancingConfig = useFinancingConfig(creditApplication?.financingProgramId)
  const [merchantFinancingConfig] = useMerchantFinancingConfig(merchant?.id, creditApplication?.financingProgramId)
  const financingConfig = merchantFinancingConfig ? merchantFinancingConfig : programFinancingConfig

  const worksheetYupResolver = useMemo(
    () => yupResolver(buildEditPersonalLoanWorksheetDtoSchema(financingConfig)),
    [financingConfig],
  )

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = useForm<EditPersonalLoanWorksheetDto>({
    mode: 'onBlur',
    defaultValues: {} as EditPersonalLoanWorksheetDto,
    resolver: worksheetYupResolver,
    context: { loanPurposeId: creditApplication?.loanPurposeId },
  })

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [updateWorksheet, isUpdatingWorksheet] = useUpdatePersonalLoanWorksheet()
  const [computeFunding, isComputingFunding] = useComputePersonalLoanFunding()
  const [deleteMessage] = useDeleteMessage()
  const theme = useTheme()

  const { id: creditAppId, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, creditAppId)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0
  const isLoading = isUpdatingWorksheet
  const error = useBusinessErrors()
  const setBusinessErrors = useSetBusinessErrors()
  const hasCVT = useHasCVT()

  // Get worksheet and private messages from context instead of Redux
  const { worksheet, privateMessages } = useCreditApplicationData()
  const personalLoanWorksheet = worksheet as PersonalLoanWorksheet | undefined

  // Local state for computed info (no longer in Redux)
  const [computedInfo, setComputedInfo] = useState<FundingComputedDto>({} as FundingComputedDto)
  // True from the instant a relevant field changes until the (debounced) funding
  // recompute settles. Lets the save button disable immediately instead of waiting
  // for the debounce + API request to actually start.
  const [isComputeFundingPending, setIsComputeFundingPending] = useState(false)

  // Compute total fees and insurance inline (no Redux selectors)
  const totalFees = computedInfo?.lenderFee || 0
  const totalCoverage = computeTotalCoverageFromFunding(computedInfo)
  const disabledPromoList = useDisabledPromoCodesList(
    merchant?.planPromoRules,
    creditApplication?.normsCreditDecision,
    creditApplication?.financingProgramId ?? EFinancingProgram.Personal,
  )

  const merchantPaymentPlans = useMemo(() => {
    return merchant?.paymentPlans ?? []
  }, [merchant?.paymentPlans])
  const paymentPlanList = usePaymentPlanList(
    creditApplication?.createdOn,
    merchantPaymentPlans,
    disabledPromoList,
    personalLoanWorksheet?.paymentPlanId,
  )

  const provinceConfigs = useProvinceConfigurations()
  const aprTooHigh = useAprTooHigh(computedInfo, creditApplication?.applicant.currentAddress.stateIso, provinceConfigs)
  const user = useUser()
  const firstPaymentOn = watch('firstPaymentOn')
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const { selectedCompany } = usePreferences()
  const [isMerchantDialogOpen, setIsMerchantDialogOpen] = useState<boolean>(false)
  const [isVariableInterest, setIsVariableInterest] = useState(false)

  const possibleTerms = usePossibleTerms(creditApplication?.finalCreditDecision.maxTermDuration, isVariableInterest)

  // Compute merchant IDs to fetch from worksheet payments
  const merchantIdsToFetch = useMemo(() => {
    if (!personalLoanWorksheet?.merchantPayments || !merchant) return []
    const removedMerchantIds = [...PAY_CUSTOMER_IDS, merchant.id]
    return personalLoanWorksheet.merchantPayments
      .filter((p) => !removedMerchantIds.includes(p.merchantId))
      .map((p) => p.merchantId)
  }, [personalLoanWorksheet?.merchantPayments, merchant])

  const [fetchedMerchants, isLoadingFetchedMerchants] = useGetMerchantsByIds(merchantIdsToFetch)

  // Local state for dialog-selected merchants
  const [dialogSelectedMerchants, setDialogSelectedMerchants] = useState<Record<string, Merchant>>({})

  // Build payment merchants map from fetched data, current merchant, and dialog-selected merchants
  const paymentMerchantsByIds = useMemo(() => {
    const map: Record<string, Merchant> = {}
    if (merchant) {
      map[merchant.id] = merchant
    }
    fetchedMerchants.forEach((m) => {
      map[m.id] = m
    })
    // Merge in dialog-selected merchants
    Object.values(dialogSelectedMerchants).forEach((m) => {
      map[m.id] = m
    })
    return map
  }, [merchant, fetchedMerchants, dialogSelectedMerchants])

  const isLoadingMerchants = isLoadingFetchedMerchants
  const [highEstimateShouldDisableFields, setHighEstimateShouldDisableFields] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)
  const [completeFinalizeHighEstimate, isPending] = useCompleteFinalizeHighEstimate()

  // Get documents from context
  const { documents: docs } = useCreditApplicationData()
  // "Complete High Estimate" is only available once the required merchant invoice has been
  // created, uploaded and approved.
  const isHighEstimateInvoiceApproved = docs.some(
    (doc) => doc.typeId === EDocumentType.ClinicInvoice && doc.status === EDocumentStatus.Approved,
  )
  useEffect(() => {
    setHighEstimateShouldDisableFields(
      (personalLoanWorksheet?.isHighEstimate && merchant?.canAcceptHighEstimate === true && hasCVT) ?? false,
    )
  }, [personalLoanWorksheet, merchant, hasCVT])

  useEffect(() => {
    if (amountRequested) trigger('term').catch(reportErrorToConsole)
  }, [amountRequested, trigger])

  // dynamic values
  const payments = watch('merchantPayments')
  const paymentFrequency = watch('paymentFrequency') as EPaymentFrequency
  const paymentPlanId = watch('paymentPlanId') as EPaymentPlan
  const deliveryOn = watch('deliveryOn')
  const term = watch('term')
  const includeInsurance = watch('includeInsurance')
  const isHighEstimate = watch('isHighEstimate')
  const featureFlags = useFeatureFlags()

  // Check which terms are valid
  const [validTerms, invalidTerms, isValidTermsLoading] = useValidTerms({
    creditApplicationId: creditApplication?.id ?? '',
    financingProgramId: creditApplication?.financingProgramId ?? '',
    interestRate: creditApplication?.finalCreditDecision?.interestRate ?? 0,
    hasCoApplicant: Boolean(creditApplication?.coApplicant),
    stateIso: creditApplication?.applicant?.currentAddress?.stateIso ?? '',
    maxPmtAmount: creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
    merchantId: merchant?.id ?? '',
    paymentFrequency: personalLoanWorksheet?.paymentFrequency ?? '',
    paymentPlanId,
    deliveryOn: deliveryOn ? new DateOnly(normalizeDateTime(deliveryOn)) : null,
    firstPaymentOn: firstPaymentOn ? normalizeDateTime(firstPaymentOn) : null,
    amountRequested,
    includeInsurance,
    possibleTerms,
  })

  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(
    paymentPlanId,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
  )

  // Show error banner when selected term is marked as pmtTooHigh
  useEffect(() => {
    const maxPmtAmount = creditApplication?.finalCreditDecision.maxPmtAmount ?? 0    

    if (term && invalidTerms.get(term) === 'pmtTooHigh' && paymentFrequency) {
      const errorMsg = t('common.errors.maxPaymentExceedsMaximumAllowed', {
        maxPayment: maxPmtAmount,
      })
      setBusinessErrors([{ message: errorMsg }])
    }
  }, [
    term,
    invalidTerms,
    paymentFrequency,
    deliveryOn,
    firstPaymentOn,
    maxPaymentAmount,
    setBusinessErrors,
    t,
    computedInfo?.paymentForFrequency,
    creditApplication?.finalCreditDecision.maxPmtAmount,
    personalLoanWorksheet?.fundingThresholdExtend?.maxPmtAmount,
  ])

  const isNFl =
    creditApplication?.applicant.currentAddress.stateIso &&
    creditApplication.applicant.currentAddress.stateIso === EProvince.Newfoundland

  const highCostRateForProvince = getHighCostRateForProvince(
    provinceConfigs,
    creditApplication?.applicant.currentAddress.stateIso,
  )

  const maxPaymentForFrequencyError = useMaxPaymentError(
    paymentFrequency,
    computedInfo?.paymentForFrequency,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
    Constants.financingProgramConfigs[EFinancingProgram.Personal].validateMaxPaymentAmountInWorksheet,
  )
  const totalObligation = useEditTotalObligation(amountRequested, computedInfo)
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const setPayments = useCallback(
    (merchantPayment: MerchantPayment[]) => {
      setValue('merchantPayments', merchantPayment)
    },
    [setValue],
  )

  const possiblePaymentMethods = useMemo(
    () => getEPaymentMethodList().map((x) => ({ label: t(`EPaymentMethod.${x}`, { ns: 'enum' }), value: x })),
    [t],
  )

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.Personal}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  const selectedPlan = useMemo(
    () => merchant?.paymentPlans?.find((p) => p.id === paymentPlanId),
    [merchant?.paymentPlans, paymentPlanId],
  )

  // reset form on worksheet changed
  useEffect(() => {
    if (personalLoanWorksheet && merchant) {
      reset(personalLoanWorksheet as EditPersonalLoanWorksheetDto)
      setPayments(personalLoanWorksheet.merchantPayments ?? [])
      setValue('firstPaymentOn', personalLoanWorksheet.firstPaymentOn ?? formatDate(new Date()))
    }
  }, [personalLoanWorksheet, merchant, reset, setPayments, setValue])

  const doComputeFunding = useCallback(
    async (computeDto: FundingInfoDto) => {
      try {
        const data = await computeFunding(computeDto)
        setComputedInfo(data)
      } catch (error) {
        reportErrorToConsole(error)
      } finally {
        setIsComputeFundingPending(false)
      }
    },
    [computeFunding],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceComputeFunding = useCallback(debounce(doComputeFunding, 500), [doComputeFunding])
  // recompute funding
  useEffect(() => {
    const interestRate = creditApplication?.finalCreditDecision.interestRate ?? 0
    const hasCoApplicant = creditApplication?.coApplicant !== null
    const stateIso = creditApplication?.applicant.currentAddress.stateIso
    const requestId = nanoid()
    if (
      amountRequested >= financingConfig.minimumLoanAmount &&
      paymentFrequency &&
      term &&
      deliveryOn &&
      paymentPlanId &&
      firstPaymentOn &&
      stateIso &&
      personalLoanWorksheet &&
      merchant
    ) {
      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication?.financingProgramId,
        amountRequested,
        includeInsurance,
        insuranceCompanyId: personalLoanWorksheet.insuranceCompanyId,
        paymentFrequency,
        paymentPlanId,
        term,
        interestRate,
        hasCoApplicant,
        stateIso,
        deliveryOn: new DateOnly(deliveryOn),
        firstPaymentOn,
        requestId,
        creditApplicationId: creditApplication.id,
        merchantId: merchant.id,
      }

      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        setIsComputeFundingPending(true)
        void debounceComputeFunding(computeDto)
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()

        setComputedInfo(emptyComputedDto)
        setIsComputeFundingPending(false)
      }
    }
  }, [
    amountRequested,
    paymentFrequency,
    paymentPlanId,
    deliveryOn,
    firstPaymentOn,
    term,
    includeInsurance,
    creditApplication,
    id,
    debounceComputeFunding,
    merchant?.id,
    merchant,
    personalLoanWorksheet,
    financingConfig.minimumLoanAmount,
  ])

  useEffect(() => {
    const isVariable = Boolean(paymentPlanId && paymentPlanId !== EPaymentPlan.RegularDailyInterests)

    if (isVariable && selectedPlan) {
      const currentTerm = getValues('term')
      if (currentTerm !== selectedPlan.loanTerm) {
        setValue('term', selectedPlan.loanTerm)
      }

      if (featureFlags.useHighEstimateOnBuyDown) {
        if (!merchant?.canPayBorrowers && payments?.some((p) => PAY_CUSTOMER_IDS.includes(p.merchantId))) {
          setPayments(payments.filter((p) => !PAY_CUSTOMER_IDS.includes(p.merchantId)))
        }
      } else {
        if (payments?.some((p) => PAY_CUSTOMER_IDS.includes(p.merchantId))) {
          setPayments(payments.filter((p) => !PAY_CUSTOMER_IDS.includes(p.merchantId)))
        }
      }
    }

    setIsVariableInterest(isVariable)
  }, [getValues, paymentPlanId, payments, selectedPlan, setPayments, setValue, merchant?.canPayBorrowers, featureFlags.useHighEstimateOnBuyDown])

  // La raison pour laquelle on ne permet pas d'avoir un High Estimate et une promo en même temps
  // sur une demande nativement est que les marchants financent les promotions en général et sont 
  // souvent peu enclins à accepter que le consommateur se fasse payer directement.
  // On utilise une feature-switch pour lever/activer cette restriction.
  useEffect(() => {
    if (!featureFlags.useHighEstimateOnBuyDown && paymentPlanId !== EPaymentPlan.RegularDailyInterests && isHighEstimate) {
      setValue('isHighEstimate', false)
    }
  }, [paymentPlanId, isHighEstimate, setValue, featureFlags.useHighEstimateOnBuyDown])
  const getMerchantFees = () => {
    if (selectedPlan) return formatCurrency((selectedPlan.merchantFeeRate / 100) * amountRequested)
    return ''
  }
  // recompute first payment options and date
  const { firstPaymentDateOptions } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn: new DateOnly(deliveryOn),
    paymentFrequency,
    isHighEstimate: isHighEstimate,
    setValue,
    getValues,
  })

  const getServiceProviderTooltip = useCallback(
    () => {
      if (!featureFlags.usePersonalLoanServiceProviderName) return ''
      const isUnregisteredMerchantNameMissing = !creditApplication?.unregisteredMerchantName || creditApplication?.unregisteredMerchantName.trim() === ''
      const isStandardMerchant = !!merchant?.id && InternalMerchants.includes(merchant.id)
      const title = isUnregisteredMerchantNameMissing ?
        t('editCreditApplication.unregisteredMerchantName.missing')
        :
        <>
          {`${t('editCreditApplication.unregisteredMerchantName.label')} :`}
          <br />
          {creditApplication?.unregisteredMerchantName}
        </>
      return (isStandardMerchant || !isUnregisteredMerchantNameMissing) ? (
        <Tooltip title={title} placement="top">
          <InfoOutlineIcon color={isStandardMerchant && isUnregisteredMerchantNameMissing ? 'warning' : 'inherit'} />
        </Tooltip>
      ) : ''
    },
    [creditApplication, merchant, featureFlags.usePersonalLoanServiceProviderName, t])

  const getMerchantName = useCallback(
    (mId: string) => {
      if (creditApplication && PAY_CUSTOMER_IDS.some((n) => n === mId)) {
        const serviceProviderTooltip = getServiceProviderTooltip()
        return <>{creditApplication.applicant.firstName} {creditApplication.applicant.lastName} {serviceProviderTooltip}</>
      }
      return paymentMerchantsByIds[mId]?.name ?? ''
    },
    [paymentMerchantsByIds, getServiceProviderTooltip, creditApplication],
  )

  const handleMerchantsSelected = useCallback(
    (newMerchants: Merchant[]) => {
      // Add newly selected merchants to dialog state
      setDialogSelectedMerchants((oldDialogSelectedMerchants) => {
        const updatedMerchants = { ...oldDialogSelectedMerchants }
        newMerchants.forEach((m) => {
          updatedMerchants[m.id] = m
        })
        return updatedMerchants
      })

      const currentPayments: MerchantPayment[] = [...payments]

      newMerchants.forEach((merch: Merchant) => {
        const existing = currentPayments.find((p) => p.merchantId === merch.id)

        if (!existing) {
          currentPayments.push({
            merchantId: merch.id,
            amount: 0,
            paymentMethod: merch.defaultPaymentMethod ?? EPaymentMethod.Check,
          })
        }
      })

      setPayments(currentPayments)

      setIsMerchantDialogOpen(false)
    },
    [payments, setPayments],
  )

  const addBorrower = () => {
    const currentPayments: MerchantPayment[] = [...payments]
    const existing = currentPayments.find((p) => PAY_CUSTOMER_IDS.some((n) => n === p.merchantId))
    if (!existing) {
      currentPayments.push({
        merchantId: EPaymentMethodMerchants.PayCustomerByBankTransfer,
        amount: 0,
        paymentMethod: EPaymentMethod.BankTransfer,
      })
    }
    setPayments(currentPayments)
  }

  const onDeleteMerchantPayment = (payment: MerchantPayment) => {
    const newPayments = payments.filter((p) => p.merchantId !== payment.merchantId)

    setPayments(newPayments)
  }

  const onMerchantPaymentAmountEdited = (newAmount: string, payment: MerchantPayment) => {
    const paymentIndex = payments.indexOf(payment)
    const tempPayments: MerchantPayment[] = [...payments]
    const normalizedAmount = normalizeNumber(newAmount)

    tempPayments[paymentIndex] = {
      merchantId: payment.merchantId,
      amount: Number(normalizedAmount),
      paymentMethod: payment.paymentMethod,
    }

    setPayments(tempPayments)
  }

  const onMerchantPaymentMethodEdited = (event: ChangeEvent<HTMLInputElement>, payment: MerchantPayment) => {
    const paymentIndex = payments.indexOf(payment)
    const tempPayments: MerchantPayment[] = [...payments]
    let paymentMerchantId = payment.merchantId
    if (PAY_CUSTOMER_IDS.some((n) => n === payment.merchantId)) {
      if ((event.target.value as EPaymentMethod) === EPaymentMethod.BankTransfer) {
        paymentMerchantId = EPaymentMethodMerchants.PayCustomerByBankTransfer
      } else {
        paymentMerchantId = EPaymentMethodMerchants.PayCustomerByCheque
      }
    }
    tempPayments[paymentIndex] = {
      merchantId: paymentMerchantId,
      amount: payment.amount,
      paymentMethod: event.target.value as EPaymentMethod,
    }

    setPayments(tempPayments)
  }
  const closeConfirmDialog = useCallback(() => {
    setIsDialogOpen(false)
  }, [])
  const closeMerchantDialog = useCallback(() => {
    setIsMerchantDialogOpen(false)
  }, [])
  const handleSave = async () => {
    if (isHighEstimate) {
      setIsDialogOpen(true)
    } else {
      const isValid = await trigger()
      if (isValid) {
        const formData = getValues()
        await onFormSubmitted(formData)
      }
    }
  }
  const onFormSubmitted = async (data: EditPersonalLoanWorksheetDto) => {
    setIsSubmitting(true)
    if (isDialogOpen) {
      setIsDialogOpen(false)
    }
    const expiry = new Date(creditApplication!.expiresOn)
    const hardHitReport = creditApplication!.applicant.hardHitReport || creditApplication!.coApplicant?.hardHitReport
    const receivedOnPlus90Days = new Date(hardHitReport.receivedOn)
    receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)
    const totalPayments = normalizeNumber(sumBy(payments, 'amount')?.toString()) ?? 0
    const mainPayment = payments.find((p) => p.merchantId === merchant?.id)
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )
    try {
      if (
        isVariableInterest &&
        (!mainPayment ||
          (selectedPlan && mainPayment && (selectedPlan.merchantFeeRate / 100) * amountRequested > mainPayment.amount))
      )
        setBusinessErrors([
          {
            message: t('worksheetCommon.merchantMustHaveFees', { merchantFee: getMerchantFees() }),
          },
        ])
      else if (
        isNFl
          ? computedInfo.effectiveRate > highCostRateForProvince
          : computedInfo.effectiveRate > financingConfig.effectiveRateThreshold
      ) {
        setBusinessErrors([
          {
            message: t('worksheetCommon.effectiveRateTooHigh', '', {
              effectiveRate: computedInfo.effectiveRate,
              maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
            }),
          },
        ])
      } else if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
        setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }])
      } else if (amountRequested > availableCredit) {
        setBusinessErrors([{ message: 'worksheetCommon.amountRequestedHigherThanAvailableCredit' }])
      } else if (compareAsc(deliveryOnUTC, receivedOnPlus90Days) === 1)
        setBusinessErrors([{ message: 'worksheetCommon.activationDateReport90Days' }])
      else if (compareAsc(deliveryOnUTC, expiry) === 1)
        setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }])
      else if (totalPayments !== amountRequested) {
        setBusinessErrors([{ message: 'worksheetCommon.merchantPaymentTotalMismatch' }])
      } else {
        data.amountRequested = amountRequested
        data.status = EWorksheetStatus.Active
        data.merchantPayments = payments
        try {
          await updateWorksheet(data)
          await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
          navigate(`/Applications/${EFinancingProgram.Personal}/${creditApplication!.id}/view`, {
            replace: true,
          })
        } catch (error) {
          reportErrorToConsole(error)
        } finally {
          setIsSubmitting(false)
        }
        return
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const finalizeHighEstimate = async () => {
    const data: EditWorksheetPaymentSplitDto = {
      merchantPayments: payments,
      creditApplicationId: creditApplication!.id,
    }
    setIsSubmitting(true)
    try {
      const totalPayments = normalizeNumber(sumBy(payments, 'amount')?.toString()) ?? 0
      if (totalPayments !== amountRequested) {
        setBusinessErrors([{ message: 'worksheetCommon.merchantPaymentTotalMismatch' }])
      }
      if (!personalLoanWorksheet?.isHighEstimate) setBusinessErrors([{ message: 'worksheetCommon.notHighEstimate' }])
      if (!hasCVT) setBusinessErrors([{ message: 'worksheetCommon.optionNotAvailable' }])
      if (!isHighEstimateInvoiceApproved)
        setBusinessErrors([{ message: 'worksheetCommon.invoiceNotApproved' }])
      else {
        await completeFinalizeHighEstimate({ dto: data, creditApplicationId: creditApplication!.id })
        navigate(`/Applications/${EFinancingProgram.Personal}/${creditApplication!.id}/view`, { replace: true })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const getFilteredPaymentMethods = (payment: MerchantPayment) => {
    if (PAY_CUSTOMER_IDS.some((n) => n === payment.merchantId)) {
      return possiblePaymentMethods.filter((m) => m.value !== EPaymentMethod.Wire && m.value !== EPaymentMethod.DEFT)
    }
    return possiblePaymentMethods.filter((m) => m.value !== EPaymentMethod.BankTransfer)
  }

  const isPaymentMethodDisabled = (payment: MerchantPayment) => {
    // disabled for the time being. ALIS does not support custom merchant methods.
    // remove this disabling function once ALIS has support.
    if (PAY_CUSTOMER_IDS.includes(payment.merchantId)) return false
    return payment.paymentMethod === paymentMerchantsByIds[payment.merchantId].defaultPaymentMethod
  }

  const handleDeletingPrivateMessage = (message: CreditApplicationMessage) => {
    if (creditApplication?.id) {
      return deleteMessage({
        id: message.id,
        creditApplicationId: message.creditApplicationId,
        financingProgramId: creditApplication.financingProgramId,
      })
    }
    return null
  }

  return (
    <div>
      <PageSpinner withBackdrop isLoading={(isLoading && isLoadingCreditApplication) || isSubmitting || isPending} />

      {!isLoading && (
        <Stack>
          <PageError errors={error} />

          {featureFlags.fixPersonalLoanVariableTermLoop &&
            creditApplication &&
            computedInfo &&
            computedInfo.effectiveRate > (isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold) && (
              <Alert severity="warning">
                {t('worksheetCommon.effectiveRateTooHigh', '', {
                  effectiveRate: computedInfo.effectiveRate,
                  maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
                })}
              </Alert>
            )}
        </Stack>
      )}
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <InputTextField
            value={t(`EPlanType.${planName}`, { ns: 'enum' })}
            label={t('worksheetCommon.program')}
            disabled
          />
          <InputTextField disabled value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0} />
          <AvailableCreditTooltip availableCreditData={availableCreditData}>
            <div style={{ position: 'relative' }}>
              <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
              {creditInProgress !== 0 && (
                <ErrorOutline
                  sx={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.palette.error.main,
                    fontSize: '1.5rem',
                  }}
                />
              )}
            </div>
          </AvailableCreditTooltip>
          <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />

          {!highEstimateShouldDisableFields && (
            <div style={{ marginTop: '1%' }}>
              <Button
                variant="contained"
                disabled={
                  aprTooHigh ||
                  isComputeFundingPending ||
                  isComputingFunding ||
                  !computedInfo ||
                  isLoading ||
                  maxPaymentForFrequencyError !== null ||
                  (term && invalidTerms.get(term) === 'pmtTooHigh') ||
                  (computedInfo &&
                    computedInfo.effectiveRate >
                      (isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold))
                }
                sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
                onClick={handleSave}
              >
                {t('common.save')}
              </Button>
            </div>
          )}
          {highEstimateShouldDisableFields && (
            <div>
              <Button
                variant="contained"
                disabled={
                  aprTooHigh ||
                  isComputingFunding ||
                  !computedInfo ||
                  isLoading ||
                  (computedInfo &&
                    computedInfo.effectiveRate >
                      (isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold))
                }
                onClick={() => setHighEstimateShouldDisableFields(false)}
                sx={{ width: 'auto', whiteSpace: 'nowrap', textAlign: 'left' }}
                startIcon={<Lock />}
              >
                <div>
                  <Typography variant="body1" component="div">
                    {t('worksheetCommon.unlockToModify')}
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ fontSize: '0.75rem' }}>
                    {t('worksheetCommon.willCancelContract')}
                  </Typography>
                </div>
              </Button>
            </div>
          )}
        </Stack>
        {creditApplication && (
          <PrivateMessage
            messages={privateMessages}
            handleDeletingPrivateMessage={handleDeletingPrivateMessage}
            currentUser={user}
            creditAppCreationDate={creditApplication.createdOn}
          />
        )}
        {merchant && <MerchantPaymentInstructions merchant={merchant} />}
        <Grid container marginTop={5}>
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: '$',
                    },
                  }}
                  {...register('amountRequested')}
                  error={errors?.amountRequested}
                  label={t('worksheetCommon.amountRequested')}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="deliveryOn"
                  control={control}
                  label={t('worksheetCommon.activationDate')}
                  disablePast
                  minDate={currentDateAsEasternTimeZone}
                  disabled={creditApplication?.editLocked || highEstimateShouldDisableFields}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={firstPaymentDateOptions.map((item) => {
                    const data: SelectValueListItem = {
                      label: formatDate(item),
                      value: formatDate(item),
                    }
                    return data
                  })}
                  label={t('worksheetCommon.firstPaymentDate')}
                  {...register('firstPaymentOn')}
                  error={errors?.firstPaymentOn}
                  value={firstPaymentOn ? formatDate(firstPaymentOn) : ''}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={paymentPlanList}
                  label={t('worksheet.paymentPlan')}
                  {...register('paymentPlanId')}
                  error={errors?.paymentPlanId}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={[{ label: t('EPaymentFrequency.monthly', { ns: 'enum' }), value: EPaymentFrequency.Monthly }]}
                  label={t('worksheet.paymentFrequency')}
                  {...register('paymentFrequency')}
                  error={errors?.paymentFrequency}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                {isValidTermsLoading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 56 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : (
                  <SelectComponent
                    disabled={isVariableInterest || highEstimateShouldDisableFields}
                    items={possibleTerms.map((item) => {
                      const isInvalid = featureFlags.fixPersonalLoanVariableTermLoop && !validTerms.has(item)
                      let invalidReason = ''
                      if (isInvalid) {
                        const reason = invalidTerms.get(item)
                        const pmtTooHigh = reason === 'pmtTooHigh'
                        const aprTooHigh = computedInfo?.effectiveRate > (isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold)

                        if (pmtTooHigh && aprTooHigh) {
                          invalidReason = t('worksheetCommon.PMTAPRTooHigh')
                        } else if (pmtTooHigh) {
                          invalidReason = t('worksheetCommon.PMTTooHigh')
                        } else {
                          invalidReason = t('worksheetCommon.TCATooHigh')
                        }
                      }
                      return {
                        label: featureFlags.fixPersonalLoanVariableTermLoop
                          ? `${item} ${t('worksheet.month')}${isInvalid ? ` (${invalidReason})` : ''}`
                          : `${item} ${t('worksheet.month')}`,
                        value: item,
                        disabled: isInvalid,
                      }
                    })}
                    label={t('worksheet.term')}
                    {...register('term')}
                    error={errors?.term}
                  />
                )}
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <FormControlLabel
                  {...register('includeInsurance')}
                  control={<Checkbox defaultChecked={personalLoanWorksheet?.includeInsurance} />}
                  label={t('worksheetCommon.addProtectionPlan')}
                  disabled={highEstimateShouldDisableFields}
                />
              </Grid>

              <Grid size={12}>
                <Box
                  width="100%"
                  sx={{
                    border: 1,
                    padding: 2,
                    marginTop: 2,
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                  }}
                >
                  <Grid container marginBottom={4} justifyContent="space-between" alignItems="center" display="flex">
                    {merchant?.canAcceptHighEstimate && (
                      <Grid>
                        <Controller
                          name="isHighEstimate"
                          control={control}
                          defaultValue={false}
                          render={({ field: { onChange, value } }) => (
                            <FormControlLabel
                              onChange={(_, checked) => onChange(checked)}
                              checked={featureFlags.useHighEstimateOnBuyDown ? value : (paymentPlanId === EPaymentPlan.RegularDailyInterests ? value : false)}
                              disabled={!featureFlags.useHighEstimateOnBuyDown && paymentPlanId !== EPaymentPlan.RegularDailyInterests}
                              control={<Checkbox />}
                              label={t('worksheetCommon.checkIsHighEstimate')}
                            />
                          )}
                        />
                      </Grid>
                    )}
                    <Grid ml="auto">
                      <Stack direction="row" spacing={1}>
                        <Button
                          color="secondary"
                          variant="contained"
                          onClick={() => addBorrower()}
                          disabled={
                            payments?.some((p) => PAY_CUSTOMER_IDS.some((n) => n === p.merchantId)) ||
                            !merchant?.canPayBorrowers ||
                            merchant?.forcePaymentToBorrower ||
                            (!featureFlags.useHighEstimateOnBuyDown && isVariableInterest)
                          }
                        >
                          {t('worksheetCommon.addBorrower')}
                        </Button>
                        <Button
                          color="secondary"
                          variant="contained"
                          disabled={merchant?.forcePaymentToBorrower}
                          onClick={() => setIsMerchantDialogOpen(true)}
                        >
                          {t('worksheetCommon.addMerchantPayment')}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>

                  {payments &&
                    !isLoadingMerchants &&
                    payments.map((p, index) => (
                      <Stack key={p.merchantId} direction="row" justifyContent="space-between" spacing={2}>
                        <Typography sx={{ width: '100%', marginTop: '1rem' }}>
                          {getMerchantName(p.merchantId)}
                        </Typography>
                        <SelectComponent
                          disabled={isPaymentMethodDisabled(p)}
                          items={getFilteredPaymentMethods(p)}
                          label={t('worksheetCommon.paymentMethod')}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => onMerchantPaymentMethodEdited(e, p)}
                          value={p.paymentMethod}
                        />
                        <InputTextField
                          {...register(`merchantPayments.${index}.amount`)}
                          sx={{ width: '30rem' }}
                          label={t('worksheetCommon.paymentAmount')}
                          slotProps={{
                            input: {
                              endAdornment: '$',
                            },
                          }}
                          onBlur={(content) => onMerchantPaymentAmountEdited(content.target.value, p)}
                        />
                        <div style={{ marginTop: '1%' }}>
                          <IconButtonWithTooltip
                            disabled={isVariableInterest && p.merchantId === merchant?.id}
                            onClick={() => onDeleteMerchantPayment(p)}
                            tooltip="delete"
                            color="primary"
                            icon={<Delete />}
                          />
                        </div>
                      </Stack>
                    ))}
                  {merchant?.canAcceptHighEstimate && isHighEstimate && (
                    <Grid container justifyContent="center" mt={4}>
                      <Grid>
                        <Button
                          color="success"
                          variant="contained"
                          disabled={!isHighEstimateInvoiceApproved}
                          onClick={async () => {
                            await finalizeHighEstimate()
                          }}
                        >
                          {t('worksheetCommon.finalizeHighEstimateWorksheet')}
                        </Button>
                        {!isHighEstimateInvoiceApproved && (
                          <Typography variant="caption" component="div" sx={{ mt: 1, textAlign: 'center' }}>
                            {t('worksheetCommon.invoiceNotApproved')}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={4}>
            <Box
              sx={{
                border: 1,
                padding: 2,
                marginLeft: 5,
                paddingBottom: 10,
              }}
            >
              {isComputingFunding ? (
                <Stack justifyContent="center" alignItems="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Stack direction="column" spacing={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('worksheetCommon.fundingSummary')} :
                  </Typography>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.loanAmount')} :</Typography>
                    <Typography>{`${formatCurrency(getValues('amountRequested'))}`}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>
                      {`${t('worksheetCommon.interest')} (${
                        isVariableInterest
                          ? formatPaymentPlanForSummary(selectedPlan)
                          : `${creditApplication?.finalCreditDecision?.interestRate}%`
                      })`}
                      :{' '}
                    </Typography>
                    <Typography>{formatCurrency(computedInfo?.totalInterestAmount)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{`${t('worksheetCommon.financeFee')} (${computedInfo?.lenderFeeRate}%)`}:</Typography>
                    <Typography>{formatCurrency(totalFees)}</Typography>
                  </Stack>

                  {isVariableInterest && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography>
                        {`${t('worksheetCommon.merchantFee')} (${selectedPlan?.merchantFeeRate}%)`} :
                      </Typography>
                      <Typography>{getMerchantFees()}</Typography>
                    </Stack>
                  )}

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.protectionPlan')} :</Typography>
                    <Typography>{formatCurrency(totalCoverage)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.totalObligation')} :</Typography>
                    <Typography>{formatCurrency(totalObligation)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.annualPercentageRate')} :</Typography>
                    <Typography>{computedInfo.effectiveRate} %</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('worksheetCommon.payment')}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.maxPmtAmount')} :</Typography>
                    <Typography>{formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.paymentFrequency')} :</Typography>
                    <Typography>
                      {personalLoanWorksheet?.paymentFrequency
                        ? t(`EPaymentFrequency.${personalLoanWorksheet?.paymentFrequency}`, { ns: 'enum' })
                        : t('EPaymentFrequency.monthly', { ns: 'enum' })}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.frequencyAmount')} :</Typography>
                    <Typography>{formatCurrency(computedInfo?.paymentForFrequency)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.firstPaymentDate')} :</Typography>
                    <Typography>{formatDate(firstPaymentOn)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                    <Typography fontWeight="bold">{t('worksheetCommon.amountToBePaid')} :</Typography>
                    <Typography>{formatCurrency(getValues('amountRequested') ?? 0)}</Typography>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
        <HighEstimateDialog
          open={isDialogOpen}
          title={t('worksheetCommon.saveFinalizeHighEstimateTitle')}
          onConfirm={handleSubmit(onFormSubmitted, reportErrorToConsole)}
          onCancel={closeConfirmDialog}
          text={t('worksheetCommon.saveFinalizeHighEstimateMessage')}
          onChange={(value: boolean) => setValue('highEstimateExplainedToClient', value)}
        />
      </form>
      <SelectMerchantDialog
        open={isMerchantDialogOpen}
        title={t('worksheetCommon.addMerchantPayment')}
        onConfirm={handleMerchantsSelected}
        onCancel={closeMerchantDialog}
        allowMultiple
        financingProgramId={EFinancingProgram.Personal}
        label={t('taskManager.merchantName')}
        financingCompanyId={selectedCompany}
      />
    </div>
  )
}
export default EditPersonalLoanWorksheetPage
